import { useMemo } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import MangaCard from '@/components/base/MangaCard';
import { mangaList, categories } from '@/mocks/mangaData';

const categoryJsonLd = (catName: string) => [
  {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://qnzodog.cn' },
      { '@type': 'ListItem', position: 2, name: '漫画分类', item: 'https://qnzodog.cn/category/all' },
      { '@type': 'ListItem', position: 3, name: catName },
    ],
  },
  {
    '@type': 'CollectionPage',
    name: `刃牙漫画 - ${catName}分类`,
    description: `浏览刃牙漫画${catName}分类下的全部作品，免费在线阅读`,
  },
];

function getCategoryData(slug: string | undefined) {
  const filteredManga = (() => {
    switch (slug) {
      case 'all': return mangaList;
      case 'fighting': return mangaList.filter((m) => m.category === '格斗');
      case 'gaiden': return mangaList.filter((m) => m.category === '外传');
      case 'special': return mangaList.filter((m) => m.category === '特别篇');
      case 'popular': return mangaList.filter((m) => m.rating >= 9.2);
      case 'completed': return mangaList.filter((m) => m.status === '已完结');
      default: return mangaList;
    }
  })();

  const category = categories.find((c) => c.slug === slug) || categories[0];

  return { filteredManga, category };
}

export default function CategoryPage() {
  const params = useParams();
  const slug = params.slug as string | undefined;
  const { filteredManga, category } = useMemo(() => getCategoryData(slug), [slug]);

  return (
    <>
      <SEOHead
        title={`刃牙漫画 - ${category.name}分类 | 免费在线阅读`}
        description={`浏览刃牙漫画${category.name}分类下的全部作品，免费在线阅读。刃牙漫画免费全集，下拉式即时阅读，全部章节一次畅读。`}
        keywords={`刃牙漫画${category.name},刃牙漫画免费,免费看刃牙,刃牙漫画在线观看`}
        canonicalUrl={`https://qnzodog.cn/category/${slug || 'all'}`}
        jsonLd={categoryJsonLd(category.name)}
      />

      <main className="min-h-screen pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-col gap-8">
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                {category.name} · 刃牙漫画
              </h1>
              <p className="text-sm text-foreground-500">
                共 {filteredManga.length} 部作品 · 刃牙漫画{category.name}分类全集免费阅读
              </p>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {categories.map((cat) => {
                const isActive = cat.slug === (slug || 'all');
                return (
                  <a
                    key={cat.slug}
                    href={`/category/${cat.slug}`}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      isActive
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} text-sm`}></i>
                    {cat.name}
                  </a>
                );
              })}
            </div>

            {filteredManga.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {filteredManga.map((manga) => (
                  <MangaCard key={manga.id} {...manga} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-foreground-500">暂无该分类的漫画作品</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
}